<script setup lang="ts">
import type { MegaMenuItem } from "~~/shared/types/navigation";
import MegaMenuHook from "./MegaMenuHook.vue";
import MegaMenuColumn from "./MegaMenuColumn.vue";
import MegaMenuActions from "./MegaMenuActions.vue";

/**
 * Desktop mega menu dropdown
 *
 * 3-column layout:
 * - Left: Hook (sales pitch with headline and value prop)
 * - Middle: Products (grouped services with icons and descriptions)
 * - Right: Actions ("I need to..." links or contextual actions)
 *
 * Columns are separated by vertical dividers for visual clarity
 */

defineProps<{
  item: MegaMenuItem;
  isOpen: boolean;
}>();

const emit = defineEmits<{
  close: [];
  mouseEnter: [];
  mouseLeave: [];
}>();

function handleLinkClick() {
  emit("close");
}
</script>

<template>
  <Transition
    enter-active-class="transition duration-200 ease-out"
    enter-from-class="opacity-0 -translate-y-2"
    enter-to-class="opacity-100 translate-y-0"
    leave-active-class="transition duration-150 ease-in"
    leave-from-class="opacity-100 translate-y-0"
    leave-to-class="opacity-0 -translate-y-2"
  >
    <div
      v-if="isOpen"
      class="absolute left-0 right-0 top-full z-40 border-t-2 border-primary-500 bg-white shadow-xl"
      @mouseenter="emit('mouseEnter')"
      @mouseleave="emit('mouseLeave')"
    >
      <div class="container-page py-10">
        <div class="flex justify-evenly">
          <!-- LEFT COLUMN: Hook (sales pitch) -->
          <div class="w-60 shrink-0">
            <MegaMenuHook :hook="item.hook" />
          </div>

          <!-- Vertical divider -->
          <div class="w-px bg-neutral-200"></div>

          <!-- MIDDLE COLUMN(S): Products -->
          <template v-for="(group, index) in item.products" :key="group.id">
            <div class="w-64 shrink-0">
              <MegaMenuColumn :group="group" @link-click="handleLinkClick" />
            </div>
            <!-- Vertical divider between product columns -->
            <div
              v-if="index < item.products.length - 1"
              class="w-px bg-neutral-200"
            ></div>
          </template>

          <!-- Vertical divider -->
          <div class="w-px bg-neutral-200"></div>

          <!-- RIGHT COLUMN: Actions -->
          <div class="w-52 shrink-0">
            <MegaMenuActions :actions="item.actions" @link-click="handleLinkClick" />
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>
